import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FC, MouseEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BasePopover } from '@mgm/common/components/base/BPopover';
import { TCommissionPlan } from '../../../types';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { commissionsActions } from '../../../store/commissions/slice';
import { useCommissions } from '../../../hooks/useCommissions';
import { getMembers } from '../../../store/team/selectors';
import { ROUTES } from '../../../data';

type TCommissionPlanPopover = {
  plan: TCommissionPlan
};

const CommissionPlanPopover: FC<TCommissionPlanPopover> = ({ plan }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { fixedTypesMapNameToId } = useCommissions();
  const members = useAppSelector(getMembers);
  const { t } = useTranslation('commissions');

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    {
      name: t('editPlan'),
      value: 'edit',
      action: () => {
        const commissionPlan = {
          ...plan,
          fixedType: fixedTypesMapNameToId[plan.fixedCommission.fixedType],
          fixedAmount: plan.fixedCommission.fixedAmount,
          productCommissions: plan.productCommissions?.map((product) => ({
            id: product.variantId,
            sku: product.sku,
            title: product.title,
            displayName: product.displayName,
            price: product.price,
            compareAtPrice: product.compareAtPrice,
            image: {
              url: product.imageUrl,
              altText: product.imageUrl,
            },
            fixedType: product.fixedType === 'flat' ? 0 : 1,
            fixedAmount: product.fixedAmount,
            product: {
              id: product.variantId,
              title: product.title,
              category: {
                name: product.category,
              },
            },
          })),
        };

        const filteredMemberEmails = members.reduce((emails, member) => {
          if (member.commissionPlan?.id === plan.id) {
            emails.push(member.email);
          }
          return emails;
        }, [] as string[]);

        dispatch(commissionsActions.setCommissionMode('edit'));

        dispatch(commissionsActions.setMemberEmails(filteredMemberEmails));

        dispatch(commissionsActions.setActiveStep(1));

        dispatch(commissionsActions.setSelectedCommissionsPlan(commissionPlan));
        navigate(ROUTES.CREATE_COMMISSIONS);
      },
      isSelected: false,
    },
    {
      name: t('deletePlan'),
      value: 'delete',
      action: () => {
        const commissionPlan = {
          ...plan,
          fixedType: fixedTypesMapNameToId[plan.fixedCommission.fixedType],
          fixedAmount: plan.fixedCommission.fixedAmount,
          productCommissions: plan.productCommissions?.map((product) => ({
            id: product.variantId,
            sku: product.sku,
            title: product.title,
            displayName: product.displayName,
            price: product.price,
            compareAtPrice: product.compareAtPrice,
            image: {
              url: product.imageUrl,
              altText: product.imageUrl,
            },
            fixedType: product.fixedType === 'flat' ? 0 : 1,
            fixedAmount: product.fixedAmount,
            product: {
              id: product.variantId,
              title: product.title,
              category: {
                name: product.category,
              },
            },
          })),
        };

        dispatch(commissionsActions.setSelectedCommissionsPlan(commissionPlan));
        dispatch(commissionsActions.setToggleDeleteDialog(true));
      },
      isSelected: false,
    },
  ];

  return (
    <>
      <IconButton
        disableRipple
        onClick={handleOpen}
        sx={{
          p: 0,
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <BasePopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <Stack sx={{ p: 1 }}>
          {menuItems.map((option) => (
            <MenuItem key={option.value} onClick={option.action}>
              {option.name}
            </MenuItem>
          ))}
        </Stack>
      </BasePopover>
    </>
  );
};

export {
  CommissionPlanPopover,
};
